import React, { useEffect, useMemo, useState } from 'react';

// Expects the Torah text file to be served at: /Chumash-Processed2.txt
// Put Chumash-Processed2.txt in your app's public/ folder.

const SLOT_COUNT = 22;

function extractHebrewLettersOnly(rawText) {
  // Keep only Hebrew letters א–ת; ignore spaces/newlines/punctuation/niqqud/taamim.
  const matches = rawText.match(/[\u05D0-\u05EA]/g);
  return matches ? matches.join('') : '';
}

function TorahCode() {
  const [slots, setSlots] = useState(Array.from({ length: SLOT_COUNT }, () => ''));
  const [torahLetters, setTorahLetters] = useState('');
  const [loadError, setLoadError] = useState('');
  const [submittedLetters, setSubmittedLetters] = useState(Array.from({ length: SLOT_COUNT }, () => ''));
  const [combined, setCombined] = useState('');
  const [formError, setFormError] = useState('');

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        setLoadError('');
        const resp = await fetch('/Torah-letters.txt', { cache: 'force-cache' });
        if (!resp.ok) throw new Error(`Failed to load Torah-letters.txt (HTTP ${resp.status})`);
        const text = await resp.text();
        const lettersOnly = extractHebrewLettersOnly(text);
        if (!cancelled) setTorahLetters(lettersOnly);
      } catch (err) {
        if (!cancelled) setLoadError(err?.message || 'Failed to load Torah file.');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const maxLetterIndex = torahLetters.length;
  const disabled = Boolean(loadError) || maxLetterIndex === 0;

  const slotLettersPreview = useMemo(() => {
    if (!maxLetterIndex) return Array.from({ length: SLOT_COUNT }, () => '');
    return slots.map((val) => {
      const trimmed = String(val).trim();
      if (!trimmed) return '';
      const n = Number.parseInt(trimmed, 10);
      if (!Number.isFinite(n) || n < 1 || n > maxLetterIndex) return '';
      return torahLetters[n - 1];
    });
  }, [slots, torahLetters, maxLetterIndex]);

  function updateSlot(i, value) {
    setSlots((prev) => {
      const next = [...prev];
      next[i] = value;
      return next;
    });
  }

  function clearAll() {
    setSlots(Array.from({ length: SLOT_COUNT }, () => ''));
    setSubmittedLetters(Array.from({ length: SLOT_COUNT }, () => ''));
    setCombined('');
    setFormError('');
  }

  function submit() {
    setFormError('');

    if (!maxLetterIndex) {
      setFormError('Torah text is still loading (or failed to load).');
      return;
    }

    const computed = slots.map((val) => {
      const trimmed = String(val).trim();
      if (!trimmed) return '';
      const n = Number.parseInt(trimmed, 10);
      if (!Number.isFinite(n) || n < 1 || n > maxLetterIndex) return '';
      return torahLetters[n - 1];
    });

    if (!computed.some(Boolean)) {
      setFormError(`Fill at least one slot with a number between 1 and ${maxLetterIndex}.`);
      return;
    }

    setSubmittedLetters(computed);
    setCombined(computed.filter(Boolean).join(''));
  }

  return (
    <div style={{ padding: 24, maxWidth: 1100, margin: '0 auto' }}>
      <div style={{ marginBottom: 10, fontWeight: 700 }}>
        Total Hebrew letters available: {maxLetterIndex ? maxLetterIndex.toLocaleString() : 'Loading...'}
      </div>

      <div style={{ marginBottom: 14, lineHeight: 1.45 }}>
        <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 6 }}>Letter Count Torah Code</div>
        <div>
          Enter up to <b>{SLOT_COUNT}</b> numbers (1-indexed). Press <b>Submit</b> to reveal the letter under each slot. Each number represents a letter in the Torah, from the first number which is the first letter to the last.
          You may leave slots empty, but at least <b>one</b> slot must be valid.
        </div>
        <div style={{ marginTop: 8, fontSize: 13, opacity: 0.9 }}>
          Counting is over <b>Hebrew letters only</b> (א–ת), ignoring spaces/newlines/punctuation/niqqud.
        </div>
      </div>

      {loadError ? (
        <div style={{ background: '#ffecec', border: '1px solid #ffb3b3', padding: 12, borderRadius: 8, marginBottom: 14 }}>
          <b>Load error:</b> {loadError}
          <div style={{ marginTop: 6, fontSize: 13 }}>
            Ensure <code>torahLetters.txt</code> is in <code>public/</code> and reachable at <code>/Torah-letters.txt</code>.
          </div>
        </div>
      ) : null}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(76px, 1fr))',
          gap: 10,
          alignItems: 'start',
        }}
      >
        {slots.map((val, i) => {
          const previewLetter = slotLettersPreview[i];
          const submittedLetter = submittedLetters[i];
          const trimmed = String(val).trim();
          const parsed = Number.parseInt(trimmed, 10);
          const hasValidNumber =
            trimmed !== '' && Number.isFinite(parsed) && parsed >= 1 && parsed <= maxLetterIndex;
          const hasInvalidNumber = trimmed !== '' && !hasValidNumber;

          return (
            <div key={i} style={{ minWidth: 0, textAlign: 'center' }}>
              <input
                value={val}
                disabled={disabled}
                onChange={(e) => updateSlot(i, e.target.value)}
                type="number"
                min={1}
                max={maxLetterIndex || undefined}
                placeholder={String(i + 1)}
                style={{
                  width: '100%',
                  boxSizing: 'border-box',
                  padding: '9px 6px',
                  borderRadius: 10,
                  border: hasValidNumber
                    ? '1px solid #1f5cff'
                    : hasInvalidNumber
                      ? '1px solid #d14343'
                      : '1px solid #cfd8e3',
                  boxShadow: hasValidNumber
                    ? '0 0 0 3px rgba(31,92,255,0.12)'
                    : hasInvalidNumber
                      ? '0 0 0 3px rgba(209,67,67,0.12)'
                      : 'none',
                  background: hasValidNumber ? '#f7faff' : hasInvalidNumber ? '#fff7f7' : 'white',
                  fontSize: 15,
                  textAlign: 'center',
                  transition: 'border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease',
                }}
              />
              <div style={{ marginTop: 6, fontSize: 18, fontWeight: 800, minHeight: 24, direction: 'rtl' }}>
                {submittedLetter || previewLetter || ' '}
              </div>
              <div style={{ fontSize: 11, opacity: 0.8, minHeight: 14 }}>
                {submittedLetter ? 'result' : previewLetter ? 'preview' : ''}
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
        <button
          onClick={submit}
          disabled={disabled}
          style={{
            padding: '10px 14px',
            borderRadius: 10,
            border: '1px solid #1f3b73',
            background: '#1f5cff',
            color: 'white',
            fontWeight: 800,
            cursor: disabled ? 'not-allowed' : 'pointer',
          }}
        >
          Submit
        </button>
        <button
          onClick={clearAll}
          style={{
            padding: '10px 14px',
            borderRadius: 10,
            border: '1px solid #cfd8e3',
            background: 'white',
            color: '#1f3b73',
            fontWeight: 800,
            cursor: 'pointer',
          }}
        >
          Clear
        </button>
      </div>

      {formError ? <div style={{ marginTop: 12, color: '#b00020', fontWeight: 700 }}>{formError}</div> : null}

      {combined ? (
        <div
          style={{
            marginTop: 18,
            padding: 16,
            border: '1px solid #cfd8e3',
            borderRadius: 12,
            background: '#fbfdff',
            overflowX: 'auto',
          }}
        >
          <div style={{ fontWeight: 900, marginBottom: 8 }}>Combined output (slot order):</div>
          <div
            style={{
              fontSize: 'clamp(24px, 5vw, 32px)',
              fontWeight: 900,
              direction: 'rtl',
              lineHeight: 1.35,
              whiteSpace: 'nowrap',
              minWidth: 'fit-content',
            }}
          >
            {combined}
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default TorahCode;